<template>
  <v-container>
    <v-card :elevation="10">
      <v-card-title>
        <h3>調査情報の入力</h3>
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col
            cols="3"
            class="text-right pr-4 pt-3">
            <h3>調査の種類</h3>
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="form.type"
              v-bind="diffAttributes(previousObject.type, form.type)"
              outlined
              :items="types"
              item-value="id"
              item-text="label"
              label="調査の種類"
              :error-messages="error.type"
              dense />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="3"
            class="text-right pr-4 pt-3">
            <h3>書面調査期間</h3>
          </v-col>
          <v-col
            cols="3"
            v-bind="diffAttributes(
              previousObject.written_survey_started_at, form.written_survey_started_at)">
            <date-picker
              v-model="form.written_survey_started_at"
              :error-messages="error.written_survey_started_at" />
            <div
              v-if="hasDiff(previousObject.written_survey_started_at,
                            form.written_survey_started_at)"
              class="ml-10">
              {{ previousDate('written_survey_started_at') }}
            </div>
          </v-col>
          <v-col
            cols="3"
            v-bind="diffAttributes(
              previousObject.written_survey_finished_at, form.written_survey_finished_at)">
            <date-picker
              v-model="form.written_survey_finished_at"
              :error-messages="error.written_survey_finished_at" />
            <div
              v-if="hasDiff(previousObject.written_survey_finished_at,
                            form.written_survey_finished_at)"
              class="ml-10">
              {{ previousDate(previousObject.written_survey_finished_at,
                              form.written_survey_finished_at) }}
            </div>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="3"
            class="text-right pr-4 pt-3">
            <h3>現場調査期間</h3>
          </v-col>
          <v-col
            cols="3"
            v-bind="diffAttributes(
              previousObject.field_survey_started_at, form.field_survey_started_at)">
            <date-picker
              v-model="form.field_survey_started_at"
              :error-messages="error.field_survey_started_at" />
            <div
              v-if="hasDiff(previousObject.field_survey_started_at,
                            form.field_survey_started_at)"
              class="ml-10">
              {{ previousDate(previousObject.field_survey_started_at,
                              form.field_survey_started_at) }}
            </div>
          </v-col>
          <v-col
            cols="3"
            v-bind="diffAttributes(
              previousObject.field_survey_finished_at, form.field_survey_finished_at)">
            <date-picker
              v-model="form.field_survey_finished_at"
              :error-messages="error.field_survey_finished_at" />
            <div
              v-if="hasDiff(previousObject.field_survey_finished_at,
                            form.field_survey_finished_at)"
              class="ml-10">
              {{ previousDate(previousObject.field_survey_finished_at,
                              form.field_survey_finished_at) }}
            </div>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="3"
            class="text-right pr-4 pt-3">
            <h3>実施者</h3>
          </v-col>
          <v-col cols="8">
            <v-data-table
              dense
              disable-pagination
              hide-default-footer
              :headers="headers"
              :items="form.users"
              disable-sort
              item-key="index"
              @click:row="editItem">
              <template
                v-if="roles.length > 0"
                v-slot:item.role_name="{ item }">
                {{ roles.filter(role => role.id === item.pivot.role_id)[0].name }}
              </template>
              <template v-slot:item.pivot.assigned_place="{ item }">
                <div
                  v-bind="diffAttributes(
                    previousUserPivot(indexOfUsers(item)).assigned_place,
                    form.users[indexOfUsers(item)].pivot.assigned_place
                  )">
                  {{ item.pivot.assigned_place }}
                </div>
                <div
                  v-if="hasDiff(
                    previousUserPivot(indexOfUsers(item)).assigned_place,
                    form.users[indexOfUsers(item)].pivot.assigned_place)"
                  class="ml-10">
                  {{ previousDate(
                    previousObject.assigned_place,
                    form.users[indexOfUsers(item)].pivot.assigned_place) }}
                </div>
              </template>
              <template v-slot:item.license1.code="{ item }">
                <div
                  v-bind="diffAttributes(
                    previousUserPivot(indexOfUsers(item)).main_license_code,
                    form.users[indexOfUsers(item)].pivot.main_license_code
                  )">
                  {{ item.pivot.main_license_code }}
                </div>
                <div
                  v-if="error[`users.${indexOfUsers(item)}.pivot.main_license_code`]"
                  class="error--text">
                  <v-icon
                    color="error"
                    small>
                    mdi-alert
                  </v-icon>必須
                </div>
              </template>
            </v-data-table>
            <v-alert
              v-if="error.users"
              dense
              outlined
              type="error">
              {{ error.users[0] }}
            </v-alert>
          </v-col>
          <v-col cols="1">
            <user-picker
              v-if="!form.readonly && dialog"
              v-model="dialog"
              :item="editedItem"
              :items="form.users"
              :is-new="editedIndex === -1"
              :title="editedIndex === -1 ? '実施者の追加': '実施者の編集'"
              :roles="roles"
              :license-types="licenseTypes"
              button-text="追加"
              @close="onClickedCloseButton"
              @save="onDialogSaved">
            </user-picker>
          </v-col>
        </v-row>
      </v-card-text>
      <slot name="footer">
        <global-footer>
          <template #left>
            <v-btn
              large
              @click.prevent="onReturnClicked">
              <v-icon>mdi-chevron-left</v-icon>
              戻る
            </v-btn>
          </template>
          <template #right>
            <v-btn
              v-if="!form.readonly"
              large
              color="default"
              class="mr-1"
              :disabled="!hasChanges"
              @click.prevent="onDraftSaveClicked">
              ブラウザに一時保存
            </v-btn>
            <v-btn
              large
              color="primary"
              @click.prevent="onNextClicked">
              次へ
              <v-icon right>mdi-chevron-right</v-icon>
            </v-btn>
          </template>
        </global-footer>
      </slot>
    </v-card>
  </v-container>
</template>

<script>

import UserPicker from './UserPicker'
import DatePicker from '../DatePicker'
import {
  getStepSession, markAsUnsavedSession, setStepSession,
} from '../../logic/session'
import PreviousDiff from '../../mixins/PreviousDiff'

export default {
  name: 'ConfirmationFormStep2',
  components: { DatePicker, UserPicker },
  mixins: [PreviousDiff],
  props: {
    isNew: {
      type: Boolean,
      default: false,
    },
    types: {
      type: Array,
      default: () => [
        {
          id: 1,
          label: '１．石綿障害規則第3条に基づく事前調査',
        },
        {
          id: 2,
          label: '２．その他の事前調査',
        },
      ],
    },
    roles: {
      type: Array,
      default: () => ([]),
    },
    licenseTypes: {
      type: Array,
      default: () => ([]),
    },
    created: {
      type: Boolean,
      default: false,
    },
    applied: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        type: 2,
        written_survey_started_at: '',
        written_survey_finished_at: '',
        field_survey_started_at: '',
        field_survey_finished_at: '',
        users: [],
      },
      dialog: false,
      headers: [
        { text: '役割', value: 'role_name' },
        { text: '名前', value: 'name' },
        { text: '登録番号', value: 'license1.code' },
        { text: '担当箇所', value: 'pivot.assigned_place' },
      ],
      editedIndex: -1,
      editedItem: {
        id: null,
        pivot: {
          role_id: 0,
          main_license_code: null,
          other_license_code: null,
          other_license_type: null,
          telephone: null,
          mibilephone: null,
          fax: null,
        },
      },
      error: {},
      hasChanges: false,
    }
  },
  watch: {
    dialog(val) {
      return val || this.onDialogClosed()
    },
    hasChanges() {
      this.$emit('hasChange', this.hasChanges)
    },
  },
  mounted() {
    if (!this.isNew) {
      this.fetchItem()
    }
    this.loadItem()
  },

  methods: {
    loadItem() {
      const form = getStepSession('confirmation', 2)
      if (form) {
        this.form = form
      }
      // 変更があればフラグをセット
      this.$watch('form', () => {
        this.hasChanges = true
      }, { deep: true })
    },
    async fetchItem() {
      return true
    },
    async validateItem() {
      try {
        await window.axios.post('/api/confirmations/step2', this.form)
        this.error = {}
        return true
      } catch (e) {
        if (e.response.status === 422) {
          this.error = e.response.data.errors
        }
      }
      return false
    },
    onReturnClicked() {
      this.$emit('change', 1)
    },
    async onNextClicked() {
      if (this.form.readonly) {
        this.$emit('change', 3)
        return
      }
      if (await this.validateItem()) {
        if (this.hasChanges) {
          markAsUnsavedSession('confirmation')
        }
        setStepSession('confirmation', 2, this.form)
        this.$emit('change', 3)
      }
    },
    editItem(item) {
      this.editedIndex = this.form.users.indexOf(item)
      this.editedItem = { ...this.form.users[this.editedIndex] }
      this.dialog = true
    },
    deleteItem(item) {
      const index = this.form.users.indexOf(item)
      return window.confirm('部屋情報を削除してもよろしいですか?') // eslint-disable-line no-alert
      && this.form.users.splice(index, 1)
    },
    onClickedCloseButton() {
      this.onDialogClosed()
    },
    onDialogClosed() {
      this.dialog = false
      this.editedItem = {
        id: null,
        pivot: {
          role_id: 0,
          main_license_code: null,
          other_license_code: null,
          other_license_type: null,
          telephone: null,
          mobilephone: null,
          fax: null,
        },
      }
      this.editedIndex = -1
    },
    onDialogSaved() {
      if (this.editedIndex > -1) {
        this.$set(this.form.users, this.editedIndex, this.editedItem)
      } else {
        this.form.users.push(this.editedItem)
      }
      this.onDialogClosed()
    },
    indexOfUsers(item) {
      return this.form.users.indexOf(item)
    },
    onDraftSaveClicked() {
      setStepSession('confirmation', 2, this.form)
      markAsUnsavedSession('confirmation')
      this.hasChanges = false
    },
    previousUserPivot(index) {
      return this.previousObject.users
        ? this.previousObject.users[index].pivot ?? null
        : {}
    },
    diffAttributes(previous, current, options = null) {
      return this.created && this.applied
        ? this.$super(PreviousDiff).diffAttributes(previous, current, options)
        : false
    },
    hasDiff(previous, current) {
      return this.created && this.applied
        ? this.$super(PreviousDiff).hasDiff(previous, current)
        : false
    },
  },
}
</script>
